import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Plus } from "lucide-react";
import type { FreeformFieldOption, FreeformFilter, FreeformFilterBlock, FreeformOperation } from "@tellescope/utils";
import { Button as DSButton } from "@tellescope/design-system";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { FilterConditionRow, type FilterConditionValueRenderProps } from "./FilterConditionRow";

export interface FilterBlockEditorProps {
  block: FreeformFilterBlock;
  fieldOptions: FreeformFieldOption[];
  onChange: (block: FreeformFilterBlock) => void;
  onRemove: () => void;
  /**
   * Context-wide operation allow-list, forwarded to every row. See
   * {@link FilterConditionRowProps.allowedOperations}.
   */
  allowedOperations?: FreeformOperation[];
  /** Optional per-field value input override, forwarded to every row. */
  renderValueInput?: (props: FilterConditionValueRenderProps) => ReactNode | undefined;
  /** Labels for the add/clear affordances, so consumers can localize in their own namespace. */
  labels?: {
    addCondition?: string;
    clear?: string;
  };
  className?: string;
}

/**
 * The reusable AND/OR filter builder for a single block: renders each condition as a
 * {@link FilterConditionRow}, with an AND/OR connector chip between rows (a popover that toggles
 * the block's `logic`), an "add condition" affordance, and a "clear" affordance. Feature-agnostic —
 * all data-model specifics arrive via `fieldOptions`, `allowedOperations`, and `renderValueInput`.
 */
const makeEmptyFilter = (fieldOptions: FreeformFieldOption[]): FreeformFilter => ({
  field: fieldOptions[0]?.value ?? "",
  operation: "equals",
  value: undefined,
});

export const FilterBlockEditor = ({
  block,
  fieldOptions,
  onChange,
  onRemove,
  allowedOperations,
  renderValueInput,
  labels,
  className,
}: FilterBlockEditorProps): ReactNode => {
  const { t } = useTranslation();

  const updateFilter = (index: number, next: FreeformFilter) => {
    onChange({ ...block, filters: block.filters.map((filter, i) => (i === index ? next : filter)) });
  };

  const removeFilter = (index: number) => {
    onChange({ ...block, filters: block.filters.filter((_, i) => i !== index) });
  };

  const addFilter = () => {
    onChange({ ...block, filters: [...block.filters, makeEmptyFilter(fieldOptions)] });
  };

  return (
    <div className={className}>
      <div className="flex flex-col items-center">
        {block.filters.map((filter, index) => (
          // Conditions within a block have no stable id; the index is part of the key by necessity.
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`${block.id}-${index}-${filter.field}`}>
            <FilterConditionRow
              filter={filter}
              fieldOptions={fieldOptions}
              allowedOperations={allowedOperations}
              renderValueInput={renderValueInput}
              onChange={(next) => updateFilter(index, next)}
              onRemove={() => {
                if (block.filters.length === 1) {
                  onRemove();
                } else {
                  removeFilter(index);
                }
              }}
              variant={block.filters.length > 1 ? block.logic : "and"}
            />
            {index !== block.filters.length - 1 && (
              <>
                <div className={cn("h-2 w-[2px] bg-orange-300", block.logic === "and" && "bg-transparent")} />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="secondary"
                      className={cn(
                        "text-xs rounded-full text-muted-foreground gap-1",
                        block.logic === "or" ? "bg-orange-100 text-orange-700 hover:bg-orange-200" : "bg-gray-100"
                      )}
                    >
                      {block.logic === "or" ? t("OR") : t("AND")}
                      <ChevronDown className={block.logic === "or" ? "text-orange-700" : "text-muted-foreground"} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="flex flex-col w-[100px] items-start p-1" align="start">
                    <Button
                      variant="ghost"
                      className="text-xs font-light justify-between w-full"
                      onClick={() => onChange({ ...block, logic: "and" })}
                    >
                      {t("AND")}
                      {block.logic === "and" && <Check />}
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-xs font-light justify-between w-full"
                      onClick={() => onChange({ ...block, logic: "or" })}
                    >
                      {t("OR")}
                      {block.logic === "or" && <Check />}
                    </Button>
                  </PopoverContent>
                </Popover>
                <div className={cn("h-2 w-[2px] bg-orange-300", block.logic === "and" && "bg-transparent")} />
              </>
            )}
          </Fragment>
        ))}
      </div>
      <div className="flex justify-between items-center py-2 w-full">
        <DSButton variant="ghost" color="muted" size="sm" onClick={addFilter}>
          <Plus className="h-4 w-4" />
          {labels?.addCondition ?? t("Add condition")}
        </DSButton>
        <DSButton variant="ghost" color="muted" size="sm" onClick={onRemove}>
          {labels?.clear ?? t("Clear")}
        </DSButton>
      </div>
    </div>
  );
};
